import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Phone, Mail, Download, Archive, StickyNote } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl, LEAD_STATUSES } from "../lib/api";

export default function LeadDetail() {
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [note, setNote] = useState("");

  useEffect(() => { api.get(`/leads/${id}`).then((r) => setLead(r.data)).catch(() => toast.error("Lead not found")); }, [id]);

  if (!lead) return <p className="text-slate-400">Loading…</p>;

  const setStatus = async (status) => {
    const { data } = await api.patch(`/leads/${id}`, { status });
    setLead(data);
    toast.success(`Status updated to ${status}`);
  };

  const addNote = async () => {
    if (!note.trim()) return;
    const { data } = await api.post(`/leads/${id}/notes`, { text: note });
    setLead({ ...lead, notes: [...(lead.notes || []), data] });
    setNote("");
  };

  const toggleArchive = async () => {
    const { data } = await api.patch(`/leads/${id}`, { archived: !lead.archived });
    setLead(data);
    toast.success(data.archived ? "Lead archived" : "Lead restored");
  };

  const download = () => {
    const blob = new Blob([JSON.stringify(lead, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `lead-${lead.id.slice(0, 8)}.json`;
    a.click();
  };

  const fields = [
    ["Full Name", lead.name], ["Phone", lead.phone], ["Email", lead.email || "—"],
    ["Address", lead.address || "—"], ["City", lead.city || "—"], ["ZIP", lead.zip_code || "—"],
    ["Service", lead.service], ["Preferred Contact", lead.contact_method || "—"],
    ["Preferred Date", lead.preferred_date || "—"], ["Preferred Time", lead.preferred_time || "—"],
    ["Source", lead.source],
    ["Received", new Date(lead.created_at).toLocaleString()],
  ];

  return (
    <div data-testid="portal-lead-detail">
      <Link to="/portal/leads" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#0B1B3D] mb-6" data-testid="lead-back-link">
        <ArrowLeft className="w-4 h-4" /> Back to leads
      </Link>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading font-extrabold text-[#0B1B3D] text-2xl">{lead.name}</h1>
          <p className="text-slate-500 text-sm">Lead #{lead.id.slice(0, 8).toUpperCase()} {lead.archived && <span className="text-red-500 font-bold">• ARCHIVED</span>}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a href={`tel:${lead.phone}`} className="btn-primary !py-2.5 !px-4 !text-xs" data-testid="lead-call-btn"><Phone className="w-4 h-4" /> Call</a>
          {lead.email && <a href={`mailto:${lead.email}`} className="btn-outline-dark !py-2.5 !px-4 !text-xs" data-testid="lead-email-btn"><Mail className="w-4 h-4" /> Email</a>}
          <button onClick={download} className="btn-outline-dark !py-2.5 !px-4 !text-xs" data-testid="lead-download-btn"><Download className="w-4 h-4" /> Download</button>
          <button onClick={toggleArchive} className="btn-outline-dark !py-2.5 !px-4 !text-xs" data-testid="lead-archive-btn"><Archive className="w-4 h-4" /> {lead.archived ? "Restore" : "Archive"}</button>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200 p-6">
            <h2 className="font-heading font-bold text-[#0B1B3D] mb-5">Customer Information</h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              {fields.map(([k, v]) => (
                <div key={k}>
                  <dt className="field-label !mb-0.5">{k}</dt>
                  <dd className="text-slate-700 font-medium">{v}</dd>
                </div>
              ))}
            </dl>
            {lead.description && (
              <div className="mt-6 bg-slate-50 border border-slate-100 p-4">
                <p className="field-label">Project Description</p>
                <p className="text-slate-700 whitespace-pre-line">{lead.description}</p>
              </div>
            )}
          </div>
          {lead.photos?.length > 0 && (
            <div className="bg-white border border-slate-200 p-6">
              <h2 className="font-heading font-bold text-[#0B1B3D] mb-5">Project Photos ({lead.photos.length})</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {lead.photos.map((p, i) => (
                  <a key={i} href={imgUrl(p)} target="_blank" rel="noreferrer" data-testid={`lead-photo-${i}`}>
                    <img src={imgUrl(p)} alt={`Project photo ${i + 1}`} className="w-full h-40 object-cover border border-slate-200 hover:opacity-90" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 p-6">
            <h2 className="font-heading font-bold text-[#0B1B3D] mb-4">Status</h2>
            <div className="flex flex-col gap-2">
              {LEAD_STATUSES.map((s) => (
                <button key={s} onClick={() => setStatus(s)} data-testid={`status-btn-${s.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-4 py-2.5 text-left font-heading font-semibold text-sm border transition-colors ${
                    lead.status === s ? "bg-[#005AE0] text-white border-[#005AE0]" : "bg-white text-slate-600 border-slate-200 hover:border-[#005AE0]"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div className="bg-white border border-slate-200 p-6">
            <h2 className="font-heading font-bold text-[#0B1B3D] mb-4 flex items-center gap-2"><StickyNote className="w-4 h-4 text-[#005AE0]" /> Internal Notes</h2>
            <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
              {(lead.notes || []).map((n) => (
                <div key={n.id} className="bg-slate-50 border border-slate-100 p-3 text-sm">
                  <p className="text-slate-700">{n.text}</p>
                  <p className="text-xs text-slate-400 mt-1">{n.author} • {new Date(n.created_at).toLocaleString()}</p>
                </div>
              ))}
              {(!lead.notes || lead.notes.length === 0) && <p className="text-sm text-slate-400">No notes yet.</p>}
            </div>
            <textarea className="field mb-3" rows={3} placeholder="Add an internal note…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="lead-note-input" />
            <button onClick={addNote} className="btn-primary !py-2.5 !px-5 !text-xs w-full" data-testid="lead-note-submit">Add Note</button>
          </div>
        </div>
      </div>
    </div>
  );
}
